import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { useComparator } from '../context/ComparatorContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { FaSearch, FaPlus, FaTimes, FaFilePdf, FaExchangeAlt, FaCheck, FaTrophy, FaArrowUp, FaArrowDown, FaEquals, FaInfoCircle, FaStar, FaShare, FaTrash, FaEnvelope, FaClipboardList, FaStore, FaMapMarkerAlt } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PageSeo from '../components/PageSeo';
import { SEO } from '../utils/seoConfig';
import { formatPrice, getCurrencyByLocation } from '../utils/currency';

export default function Comparator() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const { comparatorData, removeProduct, clearComparator, getShareUrl } = useComparator();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showOnlyWithSpecs, setShowOnlyWithSpecs] = useState(false);
  const comparisonRef = useRef(null);
  
  // Group Quote Modal State
  const [showGroupQuoteModal, setShowGroupQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    country: 'France',
    message: ''
  });
  const [sendingQuote, setSendingQuote] = useState(false);

  // Load products from comparator context or URL params
  useEffect(() => {
    const idsParam = searchParams.get('ids');
    if (idsParam) {
      // Load from URL
      const ids = idsParam.split(',');
      loadProductsByIds(ids);
    } else if (comparatorData.products.length > 0) {
      // Load from context
      loadProductsByIds(comparatorData.products.map(p => p.id));
    }
  }, [searchParams, comparatorData]);

  const loadProductsByIds = async (ids) => {
    try {
      setLoading(true);
      const productPromises = ids.map(id => axios.get(`${API}/products/${id}`));
      const responses = await Promise.all(productPromises);
      const loadedProducts = responses.map(r => r.data).filter(Boolean);
      setSelectedProducts(loadedProducts);
      
      // Auto-compare if we have 2+ products
      if (loadedProducts.length >= 2) {
        compareProductsAuto(loadedProducts);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const compareProductsAuto = async (productsToCompare) => {
    try {
      setComparing(true);
      const response = await axios.post(`${API}/comparator/compare`, {
        product_ids: productsToCompare.map(p => p.id)
      });
      setComparisonResult(response.data);
    } catch (error) {
      console.error('Compare error:', error);
    } finally {
      setComparing(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchEquipmentTypes();
  }, [search, categoryFilter, typeFilter, showOnlyWithSpecs]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let url = `${API}/comparator/products?`;
      const params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (categoryFilter !== 'all') params.push(`category_id=${categoryFilter}`);
      if (typeFilter !== 'all') params.push(`type_equipement=${typeFilter}`);
      if (showOnlyWithSpecs) params.push('has_specs_only=true');
      
      const response = await axios.get(url + params.join('&'));
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchEquipmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/comparator/equipment-types`);
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
    }
  };

  const toggleProductSelection = (product) => {
    if (selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      if (selectedProducts.length >= 4) {
        toast.warning(i18n.language === 'fr' ? 'Maximum 4 produits' : 'Maximum 4 products');
        return;
      }
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const removeSelectedProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    // Also remove from comparator context
    removeProduct(productId);
    setComparisonResult(null);
  };

  // Share comparison URL
  const handleShare = () => {
    const url = getShareUrl() || `${window.location.origin}/comparateur?ids=${selectedProducts.map(p => p.id).join(',')}`;
    
    if (navigator.share) {
      navigator.share({
        title: i18n.language === 'fr' ? 'Comparatif GEOTS' : 'GEOTS Comparison',
        text: i18n.language === 'fr' ? 'Voir ma comparaison de produits' : 'View my product comparison',
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success(i18n.language === 'fr' ? 'Lien copié !' : 'Link copied!');
    }
  };

  // Clear all products
  const handleClearAll = () => {
    setSelectedProducts([]);
    setComparisonResult(null);
    clearComparator();
  };

  // Send group quote request
  const handleSendGroupQuote = async () => {
    if (!quoteForm.name || !quoteForm.email) {
      toast.error(i18n.language === 'fr' ? 'Nom et email requis' : 'Name and email required');
      return;
    }

    setSendingQuote(true);
    try {
      // Get unique shop IDs
      const shopIds = [...new Set(selectedProducts.map(p => p.shop_id).filter(Boolean))];
      
      // Create quote request for each shop
      for (const shopId of shopIds) {
        const productsForShop = selectedProducts.filter(p => p.shop_id === shopId);
        
        await axios.post(`${API}/quotes/request`, {
          product_id: productsForShop[0]?.id,
          user_name: quoteForm.name,
          user_email: quoteForm.email,
          user_phone: quoteForm.phone,
          company: quoteForm.company,
          message: quoteForm.message + `\n\n[Devis groupé depuis le comparateur]\nProduits demandés:\n${productsForShop.map(p => `- ${p.name_fr || p.name} (${p.brand || ''} ${p.model || ''})`).join('\n')}`
        }, {
          params: user?.id ? { user_id: user.id } : {}
        });
      }

      toast.success(
        i18n.language === 'fr' 
          ? `Demande envoyée à ${shopIds.length} vendeur(s) !` 
          : `Request sent to ${shopIds.length} seller(s)!`
      );
      setShowGroupQuoteModal(false);
      setQuoteForm({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        company: user?.company || '',
        country: 'France',
        message: ''
      });
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending request');
    } finally {
      setSendingQuote(false);
    }
  };

  const compareProducts = async () => {
    if (selectedProducts.length < 2) {
      toast.error(i18n.language === 'fr' ? 'Sélectionnez au moins 2 produits' : 'Select at least 2 products');
      return;
    }

    setComparing(true);
    try {
      const response = await axios.post(`${API}/comparator/compare`, {
        product_ids: selectedProducts.map(p => p.id)
      });
      setComparisonResult(response.data);
    } catch (error) {
      console.error('Error comparing products:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la comparaison' : 'Comparison error');
    } finally {
      setComparing(false);
    }
  };

  const exportToPDF = async () => {
    if (!comparisonRef.current) return;

    toast.loading(i18n.language === 'fr' ? 'Génération du PDF...' : 'Generating PDF...');
    
    try {
      const canvas = await html2canvas(comparisonRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`comparatif-technique-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.dismiss();
      toast.success(i18n.language === 'fr' ? 'PDF exporté !' : 'PDF exported!');
    } catch (error) {
      toast.dismiss();
      toast.error(i18n.language === 'fr' ? 'Erreur export PDF' : 'PDF export error');
    }
  };

  const getValueDisplay = (value, unit) => {
    if (value === null || value === undefined) return '-';
    return `${value} ${unit}`;
  };

  const getPerformanceIcon = (fieldKey, productId, differences) => {
    const diff = differences[fieldKey];
    if (!diff || !diff.winner_ids) return null;
    
    if (diff.winner_ids.includes(productId)) {
      return <FaTrophy className="text-yellow-500 ml-1" title={i18n.language === 'fr' ? 'Meilleur' : 'Best'} />;
    }
    
    const productValue = diff.values.find(v => v.product_id === productId)?.value;
    if (productValue === null || productValue === undefined) return null;
    
    if (diff.higher_is_better) {
      if (productValue === diff.min && diff.min !== diff.max) {
        return <FaArrowDown className="text-red-400 ml-1" />;
      }
    } else {
      if (productValue === diff.max && diff.min !== diff.max) {
        return <FaArrowDown className="text-red-400 ml-1" />;
      }
    }
    
    return null;
  };

  const getCellHighlight = (fieldKey, productId, differences) => {
    const diff = differences[fieldKey];
    if (!diff || !diff.has_difference) return '';
    
    if (diff.winner_ids?.includes(productId)) {
      return 'bg-green-50 border-green-200';
    }
    
    return 'bg-yellow-50';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8" data-testid="comparator-page">
      <PageSeo {...SEO.comparator} />
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-sm border mb-4">
            <FaExchangeAlt className="text-[#635BFF]" />
            <span className="text-sm font-medium text-gray-600">
              {i18n.language === 'fr' ? 'Outil professionnel' : 'Professional tool'}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0A2540] mb-4">
            {i18n.language === 'fr' ? 'Comparateur Technique' : 'Technical Comparator'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {i18n.language === 'fr' 
              ? 'Comparez les caractéristiques techniques de vos équipements géotechniques côte à côte'
              : 'Compare technical specifications of your geotechnical equipment side by side'}
          </p>
        </div>

        {/* Selected Products Bar */}
        {selectedProducts.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border border-[#635BFF]/20">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-semibold text-gray-700">
                {comparatorData.category_name ? (
                  <>
                    <Badge variant="outline" className="mr-2">{comparatorData.category_name}</Badge>
                    {selectedProducts.length}/3 {i18n.language === 'fr' ? 'produits' : 'products'}
                  </>
                ) : (
                  <>
                    {i18n.language === 'fr' ? 'Produits sélectionnés' : 'Selected products'} ({selectedProducts.length}/3)
                  </>
                )}
              </span>
              <div className="flex flex-wrap gap-2 flex-1">
                {selectedProducts.map(product => (
                  <Badge 
                    key={product.id} 
                    variant="secondary" 
                    className="bg-[#635BFF]/10 text-[#635BFF] px-3 py-1.5 flex items-center gap-2"
                  >
                    {product.images?.[0] && (
                      <img src={product.images[0]} alt="" className="w-6 h-6 rounded object-cover" />
                    )}
                    <span className="max-w-[150px] truncate">{product.name_fr}</span>
                    <button 
                      onClick={() => removeSelectedProduct(product.id)}
                      className="ml-1 hover:text-red-500 transition-colors"
                    >
                      <FaTimes />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {selectedProducts.length >= 2 && (
                  <>
                    <Button 
                      onClick={handleShare}
                      variant="outline"
                      size="sm"
                      className="text-sm"
                    >
                      <FaShare className="mr-1" />
                      {i18n.language === 'fr' ? 'Partager' : 'Share'}
                    </Button>
                    <Button 
                      onClick={() => setShowGroupQuoteModal(true)}
                      variant="outline"
                      size="sm"
                      className="text-sm border-[#635BFF] text-[#635BFF]"
                    >
                      <FaEnvelope className="mr-1" />
                      {i18n.language === 'fr' ? 'Devis groupé' : 'Group Quote'}
                    </Button>
                  </>
                )}
                <Button 
                  onClick={handleClearAll}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <FaTrash className="mr-1" />
                  {i18n.language === 'fr' ? 'Vider' : 'Clear'}
                </Button>
                <Button 
                  onClick={compareProducts}
                  disabled={selectedProducts.length < 2 || comparing}
                  className="bg-[#635BFF] hover:bg-[#5248d9]"
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <span className="animate-pulse">{i18n.language === 'fr' ? 'Comparaison...' : 'Comparing...'}</span>
                  ) : (
                    <>
                      <FaExchangeAlt className="mr-2" />
                      {i18n.language === 'fr' ? 'Comparer' : 'Compare'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Comparison Result */}
        {comparisonResult && (
          <div className="mb-8" ref={comparisonRef}>
            <Card className="shadow-xl border-2 border-[#635BFF]/20">
              <CardHeader className="bg-gradient-to-r from-[#0A2540] to-[#1a365d] text-white rounded-t-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <FaExchangeAlt />
                      {i18n.language === 'fr' ? 'Résultat de la Comparaison' : 'Comparison Result'}
                    </CardTitle>
                    <p className="text-white/80 mt-1">
                      {i18n.language === 'fr' 
                        ? `${comparisonResult.products.length} produits comparés`
                        : `${comparisonResult.products.length} products compared`}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold">{comparisonResult.similarity_score}%</div>
                      <div className="text-xs text-white/70">
                        {i18n.language === 'fr' ? 'Similarité' : 'Similarity'}
                      </div>
                    </div>
                    <Button 
                      onClick={exportToPDF}
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                      data-testid="export-pdf-btn"
                    >
                      <FaFilePdf className="mr-2" />
                      {i18n.language === 'fr' ? 'Exporter PDF' : 'Export PDF'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  {/* Product Headers */}
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="p-4 text-left font-semibold text-gray-600 min-w-[200px] sticky left-0 bg-gray-50 z-10">
                        {i18n.language === 'fr' ? 'Caractéristique' : 'Specification'}
                      </th>
                      {comparisonResult.products.map(product => (
                        <th key={product.id} className="p-4 text-center min-w-[220px]">
                          <Link to={`/product/${product.id}`} className="block hover:opacity-80 transition-opacity">
                            {product.images?.[0] && (
                              <img 
                                src={product.images[0]} 
                                alt={product.name_fr}
                                className="w-20 h-20 object-cover rounded-lg mx-auto mb-2 border"
                              />
                            )}
                            <div className="font-bold text-[#0A2540] line-clamp-2">{product.name_fr}</div>
                            {(product.brand || product.model) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {product.brand} {product.model}
                              </div>
                            )}
                          </Link>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Identity Section */}
                    <tr className="bg-[#635BFF]/5">
                      <td colSpan={comparisonResult.products.length + 1} className="px-4 py-2 font-bold text-[#635BFF]">
                        {i18n.language === 'fr' ? '📋 Identité' : '📋 Identity'}
                      </td>
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">{i18n.language === 'fr' ? 'Marque' : 'Brand'}</td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center font-medium">
                          {p.specs?.marque || p.brand || '-'}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">{i18n.language === 'fr' ? 'Modèle' : 'Model'}</td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center font-medium">
                          {p.specs?.modele || p.model || '-'}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">{i18n.language === 'fr' ? 'Type' : 'Type'}</td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center">
                          {p.specs?.type_equipement ? (
                            <Badge variant="secondary">{p.specs.type_equipement}</Badge>
                          ) : '-'}
                        </td>
                      ))}
                    </tr>

                    {/* Performance Section */}
                    <tr className="bg-[#00D4FF]/5">
                      <td colSpan={comparisonResult.products.length + 1} className="px-4 py-2 font-bold text-[#00D4FF]">
                        {i18n.language === 'fr' ? '⚡ Performance' : '⚡ Performance'}
                      </td>
                    </tr>
                    {Object.entries(comparisonResult.differences).map(([fieldKey, diff]) => (
                      <tr key={fieldKey} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-gray-700 sticky left-0 bg-white">
                          <div className="flex items-center gap-2">
                            <span>{diff.icon}</span>
                            <span>{i18n.language === 'fr' ? diff.label_fr : diff.label_en}</span>
                            {diff.has_difference && (
                              <Badge variant="outline" className="text-xs ml-auto">
                                {diff.variance}% {i18n.language === 'fr' ? 'écart' : 'var'}
                              </Badge>
                            )}
                          </div>
                        </td>
                        {comparisonResult.products.map(product => {
                          const value = diff.values.find(v => v.product_id === product.id)?.value;
                          return (
                            <td 
                              key={product.id} 
                              className={`p-3 text-center border ${getCellHighlight(fieldKey, product.id, comparisonResult.differences)}`}
                            >
                              <div className="flex items-center justify-center font-semibold">
                                {getValueDisplay(value, diff.unit)}
                                {getPerformanceIcon(fieldKey, product.id, comparisonResult.differences)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* Template Fields Section (Fiche Technique) */}
                    {comparisonResult.template_differences && Object.keys(comparisonResult.template_differences).length > 0 && (
                      <>
                        <tr className="bg-[#635BFF]/10">
                          <td colSpan={comparisonResult.products.length + 1} className="px-4 py-3 font-bold text-[#635BFF]">
                            <div className="flex items-center gap-2">
                              <FaClipboardList />
                              {comparisonResult.template?.name_fr || (i18n.language === 'fr' ? '📋 Fiche Technique' : '📋 Technical Sheet')}
                            </div>
                          </td>
                        </tr>
                        {Object.entries(comparisonResult.template_differences).map(([fieldKey, diff]) => {
                          if (!diff.values || diff.values.length === 0) return null;
                          
                          return (
                            <tr key={fieldKey} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-gray-700 sticky left-0 bg-white">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">
                                    {i18n.language === 'fr' ? diff.label_fr : diff.label_en}
                                  </span>
                                  {diff.unit && (
                                    <span className="text-xs text-gray-400">({diff.unit})</span>
                                  )}
                                </div>
                              </td>
                              {comparisonResult.products.map(product => {
                                const valueObj = diff.values.find(v => v.product_id === product.id);
                                const value = valueObj?.value;
                                const isWinner = diff.winner_ids?.includes(product.id);
                                
                                return (
                                  <td 
                                    key={product.id} 
                                    className={`p-3 text-center border ${isWinner && diff.has_difference ? 'bg-yellow-50' : ''}`}
                                  >
                                    <div className="flex items-center justify-center gap-1 font-semibold">
                                      {value !== undefined && value !== null ? (
                                        <>
                                          {typeof value === 'boolean' ? (
                                            value ? <FaCheck className="text-green-500" /> : <FaTimes className="text-gray-300" />
                                          ) : (
                                            <span>{value}</span>
                                          )}
                                          {diff.unit && typeof value !== 'boolean' && (
                                            <span className="text-xs text-gray-400 ml-1">{diff.unit}</span>
                                          )}
                                          {isWinner && diff.has_difference && (
                                            <FaStar className="text-yellow-500 ml-1" title={i18n.language === 'fr' ? 'Meilleure valeur' : 'Best value'} />
                                          )}
                                        </>
                                      ) : (
                                        <span className="text-gray-300">-</span>
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </>
                    )}

                    {/* Methods Section */}
                    {comparisonResult.list_comparisons?.methodes_compatibles && (
                      <>
                        <tr className="bg-green-50">
                          <td colSpan={comparisonResult.products.length + 1} className="px-4 py-2 font-bold text-green-700">
                            {i18n.language === 'fr' ? '🔧 Méthodes Compatibles' : '🔧 Compatible Methods'}
                          </td>
                        </tr>
                        {comparisonResult.list_comparisons.methodes_compatibles.all_values.map(method => (
                          <tr key={method} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-gray-700 sticky left-0 bg-white text-sm">{method}</td>
                            {comparisonResult.products.map(product => {
                              const hasMethod = comparisonResult.list_comparisons.methodes_compatibles.matrix[method]?.[product.id];
                              return (
                                <td key={product.id} className="p-3 text-center">
                                  {hasMethod ? (
                                    <FaCheck className="text-green-500 mx-auto" />
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Options Section */}
                    {comparisonResult.list_comparisons?.options_disponibles && (
                      <>
                        <tr className="bg-purple-50">
                          <td colSpan={comparisonResult.products.length + 1} className="px-4 py-2 font-bold text-purple-700">
                            {i18n.language === 'fr' ? '⚙️ Options Disponibles' : '⚙️ Available Options'}
                          </td>
                        </tr>
                        {comparisonResult.list_comparisons.options_disponibles.all_values.map(option => (
                          <tr key={option} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-gray-700 sticky left-0 bg-white text-sm">{option}</td>
                            {comparisonResult.products.map(product => {
                              const hasOption = comparisonResult.list_comparisons.options_disponibles.matrix[option]?.[product.id];
                              return (
                                <td key={product.id} className="p-3 text-center">
                                  {hasOption ? (
                                    <FaCheck className="text-purple-500 mx-auto" />
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Logistics Section */}
                    <tr className="bg-orange-50">
                      <td colSpan={comparisonResult.products.length + 1} className="px-4 py-2 font-bold text-orange-700">
                        {i18n.language === 'fr' ? '🚚 Transport & Logistique' : '🚚 Transport & Logistics'}
                      </td>
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">
                        {i18n.language === 'fr' ? 'Conditions transport' : 'Transport conditions'}
                      </td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center text-sm">
                          {p.specs?.poids_transport || '-'}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">
                        {i18n.language === 'fr' ? 'Motorisation' : 'Engine type'}
                      </td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center text-sm">
                          {p.specs?.motorisation || '-'}
                        </td>
                      ))}
                    </tr>

                    {/* Pricing Section */}
                    <tr className="bg-[#635BFF]/10">
                      <td colSpan={comparisonResult.products.length + 1} className="px-4 py-2 font-bold text-[#635BFF]">
                        {i18n.language === 'fr' ? '💰 Prix' : '💰 Pricing'}
                      </td>
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">
                        {i18n.language === 'fr' ? 'Prix achat' : 'Purchase price'}
                      </td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center">
                          {p.price_new ? (
                            <span className="font-bold text-[#635BFF]">{p.price_new.toLocaleString()} €</span>
                          ) : (
                            <span className="text-gray-400">
                              {i18n.language === 'fr' ? 'Sur demande' : 'On request'}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-700 sticky left-0 bg-white">
                        {i18n.language === 'fr' ? 'Prix location/jour' : 'Rental price/day'}
                      </td>
                      {comparisonResult.products.map(p => (
                        <td key={p.id} className="p-3 text-center">
                          {p.price_rental ? (
                            <span className="font-bold text-[#00D4FF]">{p.price_rental.toLocaleString()} €/j</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
                <span>{i18n.language === 'fr' ? 'Meilleure valeur' : 'Best value'}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaTrophy className="text-yellow-500" />
                <span>{i18n.language === 'fr' ? 'Leader sur ce critère' : 'Leader on this criteria'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-50 border border-yellow-200 rounded"></div>
                <span>{i18n.language === 'fr' ? 'Différence notable' : 'Notable difference'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Product Selection Grid */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FaPlus className="text-[#635BFF]" />
              {i18n.language === 'fr' ? 'Sélectionner des produits à comparer' : 'Select products to compare'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="relative">
                <Input
                  placeholder={i18n.language === 'fr' ? 'Rechercher...' : 'Search...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder={i18n.language === 'fr' ? 'Catégorie' : 'Category'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{i18n.language === 'fr' ? 'Toutes catégories' : 'All categories'}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder={i18n.language === 'fr' ? "Type d'équipement" : 'Equipment type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{i18n.language === 'fr' ? 'Tous types' : 'All types'}</SelectItem>
                  {equipmentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {i18n.language === 'fr' ? type.label_fr : type.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Checkbox 
                  id="specs-only" 
                  checked={showOnlyWithSpecs}
                  onCheckedChange={setShowOnlyWithSpecs}
                />
                <label htmlFor="specs-only" className="text-sm text-gray-700 cursor-pointer">
                  {i18n.language === 'fr' ? 'Avec specs uniquement' : 'With specs only'}
                </label>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF] mx-auto mb-4"></div>
                <p className="text-gray-500">{i18n.language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FaInfoCircle className="mx-auto text-4xl mb-4 text-gray-300" />
                <p>{i18n.language === 'fr' ? 'Aucun produit trouvé' : 'No products found'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {products.map(product => {
                  const isSelected = selectedProducts.some(p => p.id === product.id);
                  return (
                    <div
                      key={product.id}
                      onClick={() => toggleProductSelection(product)}
                      className={`relative cursor-pointer rounded-xl border-2 p-3 transition-all hover:shadow-lg ${
                        isSelected 
                          ? 'border-[#635BFF] bg-[#635BFF]/5 shadow-md' 
                          : 'border-gray-200 hover:border-[#635BFF]/50'
                      }`}
                      data-testid={`product-card-${product.id}`}
                    >
                      {/* Selection indicator */}
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected 
                          ? 'bg-[#635BFF] border-[#635BFF]' 
                          : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <FaCheck className="text-white text-xs" />}
                      </div>

                      {/* Has specs badge */}
                      {product.has_specs && (
                        <Badge className="absolute top-2 left-2 bg-green-500 text-white text-xs">
                          {i18n.language === 'fr' ? 'Specs' : 'Specs'}
                        </Badge>
                      )}

                      {/* Product image */}
                      {product.images?.[0] ? (
                        <img 
                          src={product.images[0]} 
                          alt={product.name_fr}
                          className="w-full h-28 object-cover rounded-lg mb-2"
                        />
                      ) : (
                        <div className="w-full h-28 bg-gray-100 rounded-lg mb-2 flex items-center justify-center">
                          <span className="text-gray-400 text-4xl">📦</span>
                        </div>
                      )}

                      {/* Product info */}
                      <h3 className="font-semibold text-sm text-[#0A2540] line-clamp-2 mb-1">
                        {product.name_fr}
                      </h3>
                      {(product.brand || product.model) && (
                        <p className="text-xs text-gray-500 truncate">
                          {product.brand} {product.model}
                        </p>
                      )}
                      {product.specs_summary && (
                        <div className="mt-2 space-y-1 text-xs text-gray-600">
                          {product.specs_summary.couple_knm && (
                            <div className="flex justify-between">
                              <span>Couple:</span>
                              <span className="font-medium">{product.specs_summary.couple_knm} kNm</span>
                            </div>
                          )}
                          {product.specs_summary.profondeur_max_m && (
                            <div className="flex justify-between">
                              <span>Prof. max:</span>
                              <span className="font-medium">{product.specs_summary.profondeur_max_m} m</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Group Quote Modal */}
      <Dialog open={showGroupQuoteModal} onOpenChange={setShowGroupQuoteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FaEnvelope className="text-[#635BFF]" />
              {i18n.language === 'fr' ? 'Demande de devis groupée' : 'Group Quote Request'}
            </DialogTitle>
            <DialogDescription>
              {i18n.language === 'fr' 
                ? `Vous allez envoyer une demande aux ${[...new Set(selectedProducts.map(p => p.shop_id).filter(Boolean))].length} vendeur(s)` 
                : `You will send a request to ${[...new Set(selectedProducts.map(p => p.shop_id).filter(Boolean))].length} seller(s)`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Products summary */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
              {selectedProducts.map(p => (
                <div key={p.id} className="flex items-center gap-2 text-sm">
                  <FaStore className="text-gray-400" />
                  <span className="font-medium">{p.name_fr}</span>
                  <span className="text-gray-500">({p.brand} {p.model})</span>
                </div>
              ))}
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{i18n.language === 'fr' ? 'Nom *' : 'Name *'}</Label>
                <Input 
                  value={quoteForm.name}
                  onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Email *' : 'Email *'}</Label>
                <Input 
                  type="email"
                  value={quoteForm.email}
                  onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                <Input 
                  value={quoteForm.phone}
                  onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
              <div>
                <Label>{i18n.language === 'fr' ? 'Société' : 'Company'}</Label>
                <Input 
                  value={quoteForm.company}
                  onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})}
                  placeholder="ACME Inc."
                />
              </div>
            </div>
            
            <div>
              <Label>{i18n.language === 'fr' ? 'Pays' : 'Country'}</Label>
              <Select value={quoteForm.country} onValueChange={(v) => setQuoteForm({...quoteForm, country: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Belgique">Belgique</SelectItem>
                  <SelectItem value="Suisse">Suisse</SelectItem>
                  <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                  <SelectItem value="Maroc">Maroc</SelectItem>
                  <SelectItem value="Algérie">Algérie</SelectItem>
                  <SelectItem value="Tunisie">Tunisie</SelectItem>
                  <SelectItem value="Sénégal">Sénégal</SelectItem>
                  <SelectItem value="Côte d'Ivoire">Côte d'Ivoire</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{i18n.language === 'fr' ? 'Précisez votre besoin (optionnel)' : 'Describe your needs (optional)'}</Label>
              <Textarea 
                value={quoteForm.message}
                onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                placeholder={i18n.language === 'fr' 
                  ? "Délai souhaité, conditions particulières, quantités..." 
                  : "Desired timeline, special conditions, quantities..."}
                className="h-24"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 text-right mt-1">{quoteForm.message.length}/500</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupQuoteModal(false)}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSendGroupQuote}
              disabled={sendingQuote}
              className="bg-[#635BFF] hover:bg-[#5248d9]"
            >
              {sendingQuote ? (
                <span className="animate-pulse">{i18n.language === 'fr' ? 'Envoi...' : 'Sending...'}</span>
              ) : (
                <>
                  <FaEnvelope className="mr-2" />
                  {i18n.language === 'fr' 
                    ? `Envoyer aux ${[...new Set(selectedProducts.map(p => p.shop_id).filter(Boolean))].length} vendeur(s)` 
                    : `Send to ${[...new Set(selectedProducts.map(p => p.shop_id).filter(Boolean))].length} seller(s)`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
